import { Injectable } from '@angular/core';
import { FeatureTogglesFacade } from '@cigna/features';
import { ChatBubbleOpen, OmniChatFacade } from '@cigna/omni/chat-state';
import { OmniConversationsFacade } from '@cigna/omni/conversations-state';
import { OmniLiveAvailabilityFacade } from '@cigna/omni/live-availability-state';
import { OmniCommChannelFacade } from '@cigna/omni/comm-channel-feature';
import { StartChat } from '@cigna/omni/right-now-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import {
  BubbleClick,
  LaunchChat,
  ChatBubbleActionTypes,
} from './chat-bubble.actions';
import { ChatBubbleFacade } from './chat-bubble.facade';

@Injectable()
export class ChatBubbleEffects {
  chatBubbleClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType<BubbleClick>(ChatBubbleActionTypes.BubbleClick),
      withLatestFrom(
        this.featureTogglesFacade.featuresEnabled(['useRightNowChat']),
        this.liveAvailabilityFacade.liveChatAvailable$,
        this.featureTogglesFacade.featuresEnabled(['rightNowChatFederated']),
        this.conversationsFacade.activeChatProvider$,
        this.featureTogglesFacade.featuresEnabled(['eapGreenBubble']),
      ),
      filter(
        ([_, useRightNowChat, liveChatAvailable, eapGreenBubble]) =>
          // restrict bubble click for users without live chat and with right now chat enabled and eap green bubble indicator turned off
          liveChatAvailable || !useRightNowChat || eapGreenBubble,
      ),
      map(
        ([
          _,
          useRightNowChat,
          _liveChatAvailable,
          rightNowChatFederated,
          activeChatProvider,
          eapGreenBubble,
        ]) =>
          // activeChatProvider is needed only when rightNowChatFederated feature is turned on
          useRightNowChat && !eapGreenBubble
            ? new StartChat(rightNowChatFederated ? activeChatProvider : '')
            : new ChatBubbleOpen(),
      ),
    ),
  );

  launchChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<LaunchChat>(ChatBubbleActionTypes.LaunchChat),
        withLatestFrom(
          this.featureTogglesFacade.featuresEnabled([
            'enableNewPreChatWelcome',
          ]),
          this.featureTogglesFacade.featuresEnabled(['useRightNowChat']),
          this.featureTogglesFacade.featuresEnabled(['liveClinicianChat']),
          this.featureTogglesFacade.featuresEnabled(['liveSupportChat']),
          this.featureTogglesFacade.featuresEnabled(['personalGuideChat']),
          this.featureTogglesFacade.featuresEnabled([
            'hasRestrictedChat',
            'shouldEnableChat',
          ]),
          this.liveAvailabilityFacade.liveChatAvailable$,
          this.featureTogglesFacade.featuresEnabled(['isEvernorthEnabled']),
        ),
        tap(
          ([
            { text },
            shouldEnablePreChatWelcome,
            isUseRightNowChat,
            isLiveClinicianChat,
            isLiveSupportChat,
            isPersonalGuideChat,
            isConsumerChatEnabled,
            isLiveChatAvailable,
            isEvernorthEnabled,
          ]) => {
            if (shouldEnablePreChatWelcome) {
              if (
                (!isUseRightNowChat &&
                  !isLiveClinicianChat &&
                  !isLiveSupportChat &&
                  !isPersonalGuideChat &&
                  !isConsumerChatEnabled) ||
                !isLiveChatAvailable
              ) {
                this.omniChatFacade.openDialog();
                this.commChannelFacade.nonLiveChatClicked({
                  buttonText: 'Chat Closed',
                  buttonId: 'chatGuide',
                });
              } else {
                let paramTags = isUseRightNowChat
                  ? ['advocate', 'member']
                  : ['default'];
                paramTags = isEvernorthEnabled
                  ? ['advocate', 'evn']
                  : paramTags;
                this.commChannelFacade.interact(
                  {
                    buttonText: 'Talk with Us',
                    buttonId: 'chatGuide',
                  },
                  { tags: paramTags },
                );
              }
            } else {
              this.chatBubbleFacade.chatBubbleClick(text);
            }
          },
        ),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private featureTogglesFacade: FeatureTogglesFacade,
    private liveAvailabilityFacade: OmniLiveAvailabilityFacade,
    private conversationsFacade: OmniConversationsFacade,
    private omniChatFacade: OmniChatFacade,
    private commChannelFacade: OmniCommChannelFacade,
    private chatBubbleFacade: ChatBubbleFacade,
  ) {}
}
